import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, TrendingUp, Zap } from 'lucide-react';

export default function UpgradeBanner({ plan, daysOnFreePlan, onDismiss }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || plan !== 'free') return null;

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  const handleUpgrade = () => {
    navigate('/checkout?plan=starter&billing=monthly');
  };

  // Show different messaging based on days on free plan
  const getMessage = () => {
    if (daysOnFreePlan <= 3) {
      return {
        title: '🎉 Welcome to Commerce Index!',
        message: 'Your agents are scanning for money. Upgrade anytime to let them fix what they find — unlimited deals, products, and AI recommendations.',
        cta: 'Start 14-Day Free Trial',
        variant: 'info'
      };
    } else if (daysOnFreePlan <= 7) {
      return {
        title: '🚀 Your agents found opportunities they can\'t act on',
        message: 'On the free plan, agents can only watch. Start a Starter trial and they start fixing — unlimited deals, AI recommendations, full thedeals.ai distribution.',
        cta: 'Start 14-Day Free Trial',
        variant: 'upgrade'
      };
    } else if (daysOnFreePlan >= 14) {
      return {
        title: '⚡ Two weeks of watching. Time to act.',
        message: 'Check your Money Found statement — then let agents capture it. Starter pays for itself or you\'ll see exactly why not.',
        cta: 'Upgrade to Starter — $79/mo',
        variant: 'urgent'
      };
    } else {
      return {
        title: '💡 Unlock unlimited deals',
        message: 'Upgrade for unlimited products, deals, and AI-powered recommendations.',
        cta: 'Start 14-Day Free Trial',
        variant: 'default'
      };
    }
  };

  const msg = getMessage();

  const variantStyles = {
    info: 'bg-blue-500/10 border-blue-500/30',
    upgrade: 'bg-gradient-to-r from-[#3EF1C7]/10 to-[#7A5CFF]/10 border-[#3EF1C7]/30',
    urgent: 'bg-gradient-to-r from-[#7A5CFF]/10 to-[#3EF1C7]/10 border-[#7A5CFF]/30',
    default: 'bg-[#111A2E] border-[#22304A]'
  };

  return (
    <div className={`rounded-lg border p-4 mb-6 ${variantStyles[msg.variant]}`} data-testid="upgrade-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="text-white font-semibold mb-1">{msg.title}</h3>
          <p className="text-[#9CB0C7] text-sm mb-3">{msg.message}</p>
          <button
            onClick={handleUpgrade}
            className="px-4 py-2 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm flex items-center gap-2"
            data-testid="upgrade-button"
          >
            <Sparkles className="w-4 h-4" />
            {msg.cta}
          </button>
        </div>
        <button
          onClick={handleDismiss}
          className="text-[#9CB0C7] hover:text-white transition-colors"
          data-testid="dismiss-banner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// Subscription limit reached banner
export function LimitReachedBanner({ limitType, current, limit, onUpgrade }) {
  const defaultUpgrade = () => { window.location.href = '/checkout?plan=starter&billing=monthly'; };
  return (
    <div className="rounded-lg border-2 border-[#FFC857] bg-[#FFC857]/5 p-6 mb-6" data-testid="limit-reached-banner">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[#FFC857]/10 flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-6 h-6 text-[#FFC857]" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-lg mb-2">
            {limitType === 'product' ? '📦 Product Limit Reached' : '🎯 Deal Limit Reached'}
          </h3>
          <p className="text-[#9CB0C7] mb-4">
            {"You've"} reached your {limitType} limit ({current}/{limit}) on the FREE plan. 
            Upgrade to PRO for unlimited {limitType}s and unlock powerful features.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onUpgrade || defaultUpgrade}
              className="px-6 py-2 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-semibold flex items-center gap-2"
              data-testid="upgrade-to-pro-button"
            >
              <Zap className="w-4 h-4" />
              Remove Limit — 14-Day Free Trial
            </button>
            <button
              onClick={() => { window.location.href = '/pricing'; }}
              className="px-6 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:bg-[#1A2438] transition-colors"
            >
              View Pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
